import React, { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { HERO_SLIDES } from '../mock';
import { useI18n } from '../context/I18nContext';
import { useSlide } from '../context/SlideContext';

// Typing animation hook
function useTypewriter(text, speed = 70, holdMs = 3500) {
  const [display, setDisplay] = useState('');
  useEffect(() => {
    let i = 0;
    setDisplay('');
    if (!text) return;
    const interval = setInterval(() => {
      i += 1;
      setDisplay(text.slice(0, i));
      if (i >= text.length) clearInterval(interval);
    }, speed);
    return () => clearInterval(interval);
  }, [text, speed, holdMs]);
  return display;
}

export default function HeroSlider() {
  const { index, setIndex, total } = useSlide();
  const timerRef = useRef(null);
  const { lang, t } = useI18n();

  useEffect(() => {
    timerRef.current = setInterval(() => {
      setIndex((i) => (i + 1) % total);
    }, 6000);
    return () => clearInterval(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [total]);

  const resetTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setIndex((i) => (i + 1) % total);
    }, 6000);
  };

  const next = () => { setIndex((index + 1) % total); resetTimer(); };
  const prev = () => { setIndex((index - 1 + total) % total); resetTimer(); };

  const slide = HERO_SLIDES[index];
  const translatedSlide = t?.home?.cards?.hero_slides?.[index] || {};
  const title = translatedSlide.title || slide.title;
  const description = translatedSlide.description || slide.description;
  const typedTitle = useTypewriter(title, 70);
  const isRTL = ['ar', 'he'].includes(lang);

  return (
    <section
      id="home"
      // Responsive height scale: 440px on phones (fits 1-view), 520 on
      // small tablets, 560 on tablets, 620 on laptops, 720 on 4K desktops.
      // Background-color stays black so the image fade-in doesn't flash
      // white on slow connections.
      className="relative w-full h-[440px] sm:h-[520px] md:h-[560px] lg:h-[620px] xl:h-[720px] overflow-hidden bg-black"
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      {HERO_SLIDES.map((s, i) => (
        <div
          key={i}
          style={{ transitionDuration: '900ms' }}
          className={`absolute inset-0 transition-opacity ease-in-out ${
            i === index ? 'opacity-100' : 'opacity-0'
          }`}
        >
          {/*
            Use an <img> (not CSS background-image) so the browser can pick
            the optimal variant from srcset per viewport. Background-images
            always download the full desktop asset even on phones.
            - `object-cover` + `w-full h-full` emulates `background-size:cover`.
            - `fetchpriority="high"` on the first slide = LCP win.
            - `loading="eager"` on slide 0, `"lazy"` on the rest.
            - `srcset` uses the same Unsplash URL with different `w=` params
              so mobile doesn't pull 1920w when 640w is enough.
          */}
          <img
            src={`${s.image}${s.image.includes('?') ? '&' : '?'}w=1600&q=80`}
            srcSet={[
              `${s.image}${s.image.includes('?') ? '&' : '?'}w=640&q=75 640w`,
              `${s.image}${s.image.includes('?') ? '&' : '?'}w=1024&q=78 1024w`,
              `${s.image}${s.image.includes('?') ? '&' : '?'}w=1600&q=80 1600w`,
              `${s.image}${s.image.includes('?') ? '&' : '?'}w=2400&q=82 2400w`,
            ].join(', ')}
            sizes="100vw"
            alt=""
            aria-hidden="true"
            draggable={false}
            loading={i === 0 ? 'eager' : 'lazy'}
            fetchPriority={i === 0 ? 'high' : 'auto'}
            className="w-full h-full object-cover object-center select-none"
          />
        </div>
      ))}
      <div className="absolute inset-0 bg-black/55" />
      <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/50" />

      <div className="relative h-full z-10 flex items-center justify-center px-4 sm:px-6">
        <div
          key={index}
          // Max-width clamps title block so it never feels "stretched" on
          // ultra-wide displays. Padding ramps smoothly across breakpoints.
          className="zh-fade-in max-w-3xl w-full text-center rounded-2xl px-5 sm:px-8 md:px-14 py-8 sm:py-10 md:py-14"
          style={{
            backgroundColor: 'rgba(20, 20, 20, 0.35)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            border: '1px solid rgba(255,255,255,0.08)',
          }}
        >
          <div className="text-[#f5a623] tracking-[0.3em] sm:tracking-[0.35em] text-[10px] sm:text-xs md:text-sm font-semibold mb-4 sm:mb-5">
            {slide.label}
          </div>
          {/* Responsive display type: ramps from text-2xl on phones →
              text-6xl on 4K. min-height prevents typing-effect reflow. */}
          <h1 className="text-white text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-bold leading-tight mb-4 sm:mb-5 min-h-[52px] sm:min-h-[60px] md:min-h-[72px] lg:min-h-[84px]">
            <span className="inline-block">
              {typedTitle}
              <span className="inline-block ml-0.5 w-[3px] h-[0.9em] bg-[#f5a623] align-middle animate-pulse" />
            </span>
          </h1>
          <p className="text-white/85 text-[13px] sm:text-sm md:text-base leading-relaxed max-w-xl mx-auto">
            {description}
          </p>
        </div>
      </div>

      <button
        onClick={prev}
        aria-label="Previous slide"
        className="absolute left-2 sm:left-3 md:left-6 top-1/2 -translate-y-1/2 z-20 w-9 h-9 sm:w-11 sm:h-11 rounded-full flex items-center justify-center text-white/80 hover:text-white hover:bg-white/10 transition-colors"
      >
        <ChevronLeft className="w-5 h-5 sm:w-7 sm:h-7" />
      </button>
      <button
        onClick={next}
        aria-label="Next slide"
        className="absolute right-2 sm:right-3 md:right-6 top-1/2 -translate-y-1/2 z-20 w-9 h-9 sm:w-11 sm:h-11 rounded-full flex items-center justify-center text-white/80 hover:text-white hover:bg-white/10 transition-colors"
      >
        <ChevronRight className="w-5 h-5 sm:w-7 sm:h-7" />
      </button>

      <div className="absolute bottom-4 sm:bottom-6 left-0 right-0 z-20 flex items-center justify-center gap-1.5 sm:gap-2">
        {HERO_SLIDES.map((_, i) => (
          <button
            key={i}
            aria-label={`Go to slide ${i + 1}`}
            onClick={() => { setIndex(i); resetTimer(); }}
            className={`h-2 sm:h-2.5 rounded-full transition-all ${
              i === index ? 'bg-[#f5a623] w-5 sm:w-6' : 'w-2 sm:w-2.5 bg-white/60 hover:bg-white'
            }`}
          />
        ))}
      </div>
    </section>
  );
}
